
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import IconButton from "./IconButton.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import Icon from "@/components/UI/Icon.vue";
import { PanelContext } from "@/components/Builder/SidebarPanels.vue";
import { IconName } from "@/lib/icons/icons";
import { uiStore } from "@/store/store-accessor";

const enum Strategy {
  Stacked = "STACKED",
  StackedFirstVisible = "STACKED_FIRST_VISIBLE",
  SideBySide = "SIDE_BY_SIDE"
}

@Component({
  components: {
    Icon,
    Dropdown,
    IconButton
  }
})
export default class Popout extends Vue {
  open = false;

  @Prop()
  title!: string;

  @Prop({ default: false })
  nested!: boolean;

  @Prop({ default: 0 })
  depth!: number;

  @Prop({ default: "EditIcon" })
  icon!: IconName;

  @Prop({ default: 18 })
  iconSize!: number;

  @Prop({ default: 280 })
  width!: number;

  @Inject({ from: "panelContext", default: undefined })
  readonly panelContext?: PanelContext;

  getPositionStrategy(strategy: Strategy, menuEl: HTMLElement, depth: number) {
    const panelPosition = this.panelContext?.position || "left";
    const rect = menuEl.getBoundingClientRect();

    const paddingX = 12;
    const paddingY = 45;
    const modifier = panelPosition === "right" ? -1 : 1;

    switch (strategy) {
      case Strategy.Stacked:
        return {
          x: rect.x + paddingX * modifier,
          y: rect.y + paddingY
        };
      case Strategy.StackedFirstVisible:
        if (depth === 2) {
          return {
            x: (this.width + paddingX) * modifier + rect.x,
            y: rect.y
          };
        }

        return {
          x: rect.x + paddingX * modifier,
          y: rect.y + paddingY
        };
      case Strategy.SideBySide:
        return {
          x: (this.width + paddingX) * modifier + rect.x,
          y: rect.y
        };
      default:
        throw new TypeError(`Unsupported Popout Position: ${strategy}`);
    }
  }

  position() {
    const container = this.$refs.container as HTMLElement;

    if (container && this.nested) {
      const menuEl = container.closest(".v-menu__content") as HTMLElement;
      const strategy =
        (uiStore.settings.popoutPosition as Strategy) || "STACKED";

      if (!menuEl) return;

      const { x, y } = this.getPositionStrategy(strategy, menuEl, this.depth);

      return {
        absolute: true,
        positionX: x,
        positionY: y
      };
    }

    // Root popouts always pop below the activator
    return {
      left: true,
      bottom: true,
      offsetY: true
    };
  }
}
